import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { capitalCallReallocationCreateRoute } from "features/capital-call/CapitalCallPage";
import { getCapitalCallReallocationDraftDataRetrieveQueryKey } from "gen/endpoints/capital-call-reallocation-draft-data/capital-call-reallocation-draft-data";
import { useFundFamilyEventCreate } from "gen/endpoints/fund-family/fund-family";
import { FundFamilyEventData } from "gen/model/fundFamilyEventData";
import { FundFamilyEventTypeEnum } from "gen/model/fundFamilyEventTypeEnum";
import { fundFamilyEventCreateBody } from "gen/schemas/fund-family/fund-family";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormTextArea } from "shared/components/input/text/TextArea";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { nowAsIsoDate } from "shared/utils/DateUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export interface CapitalCallReallocationCreateModalProps {
  onCloseComplete(): void;
}

export const CapitalCallReallocationCreateModal: React.FC<
  CapitalCallReallocationCreateModalProps
> = ({ onCloseComplete }) => {
  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();
  const { t } = useTranslation(["common", "capitalCall"]);
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });

  const form = useForm<FundFamilyEventData>({
    resolver: zodResolver(fundFamilyEventCreateBody),
    defaultValues: {
      fund_family_id: fundFamilyId,
      type: FundFamilyEventTypeEnum.capital_call_reallocation,
      effective_date: nowAsIsoDate(),
      posted_date: nowAsIsoDate(),
      capital_call_effective_date: null,
      capital_call_posted_date: null,
      original_fund_family_event_id: fundFamilyEventId,
    },
  });

  const { mutateAsync: createReallocationEvent } = useFundFamilyEventCreate();

  const onSubmit = async (data: FundFamilyEventData) =>
    createReallocationEvent({
      fundFamilyId,
      data,
    });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <Form
        form={form}
        onSubmit={onSubmit}
        onSuccess={async (data: FundFamilyEventData) => {
          await invalidateQueryKeys(
            queryClient,
            getCapitalCallReallocationDraftDataRetrieveQueryKey(
              fundFamilyId,
              fundFamilyEventId,
            ),
          );
          navigate(
            capitalCallReallocationCreateRoute({
              fundFamilyId,
              fundFamilyEventId,
              reallocationFundFamilyEventId: data.id!,
            }),
          );
          onClose();
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Reallocate Capital Call Uses
            {/* {t("capitalCall:reallocation.editDateModal.title")} */}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <FormDateInput
              name="effective_date"
              title={t("common:effectiveDate")}
            />
            <FormTextArea title={t("common:notesOptional")} name="notes" />
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onClose} autoFocus={true} />}
              confirm={<FormSubmitButton>{t("common:save")}</FormSubmitButton>}
            />
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};
